import moment from 'moment';
import React, { useEffect, useReducer, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { FaRegSave } from 'react-icons/fa';
import { MdAdd, MdArrowBack } from 'react-icons/md';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from '../../toasts';
import PropTypes from 'prop-types';
import CustomTypeahead from '../../components/CustomTypeahead';
import { Button, FormField, Section, Select, controlClass, textareaClass } from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import PaymentReceiptStep from '../../components/PaymentReceiptStep/PaymentReceiptStep';
import debtService from '../../services/debt.service';
import paymentService from '../../services/payment.service';
import { currencies, getError } from '../../utils';
import { isCancelledByClose } from '../../utils/dueCancellation';

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, loading: false, error: '' };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'FETCH_DONE':
		return { ...state, loading: false };
	case 'SAVE_REQUEST':
		return { ...state, saving: true, error: '' };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false, error: '' };
	case 'SAVE_FAIL':
		return { ...state, saving: false, error: action.payload };
	case 'UPLOAD_REQUEST':
		return { ...state, uploading: true };
	case 'UPLOAD_SUCCESS':
	case 'UPLOAD_FAIL':
		return { ...state, uploading: false };
	default:
		return state;
	}
};

const emptyForm = {
	amount: 0,
	date: moment.utc().toDate(),
	debt: null,
	debtDue: null,
	folder: '',
	observations: '',
	comprobante: null,
	currency: '',
	paidBy: '',
	paymentMethod: '',
};

const paymentToForm = (payment) => {
	if (!payment) return { ...emptyForm };
	return {
		...emptyForm,
		amount: payment.amount || 0,
		date: payment.date ? moment(payment.date).toDate() : moment.utc().toDate(),
		debt: payment.debt || null,
		debtDue: payment.debtDue || null,
		folder: payment.folder || '',
		observations: payment.observations || '',
		comprobante: payment.comprobante || null,
		currency: payment.currency || '',
		paidBy: payment.paidBy || '',
		paymentMethod: payment.paymentMethod || '',
	};
};

const toDateInputValue = (value) => {
	if (!value) return '';
	const d = value instanceof Date ? value : new Date(value);
	if (isNaN(d.getTime())) return '';
	return d.toISOString().slice(0, 10);
};

const PaymentEditor = ({ mode = 'create', paymentId = null, initialData = null, onCancel, onSaved, cancelLabel = 'Cancelar', saveLabel, preloadDebtId = null, entityFilter = false }) => {
	const isEditing = mode === 'edit';
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	// Id injected via ?debtId= after a "crear nueva deuda" round trip (see
	// linkToCreate on the debt typeahead below). Only meaningful when the
	// field isn't locked by preloadDebtId.
	const injectedDebtId = !preloadDebtId ? searchParams.get('debtId') : null;
	const consumedDebtId = useRef(null);

	const [{ loading, saving, uploading, error }, dispatch] = useReducer(reducer, {
		loading: true,
		saving: false,
		uploading: false,
		error: '',
	});

	const [form, setForm] = useState(() => paymentToForm(initialData));
	const [debts, setDebts] = useState([]);
	const [generatedDues, setGeneratedDues] = useState([]);
	const [disableDebtDue, setDisableDebtDue] = useState(!initialData?.debt);
	const [unpaidAmount, setUnpaidAmount] = useState(0);
	const [fieldErrors, setFieldErrors] = useState({});

	// Entity-first narrowing (Cliente/Propiedad), only fetched/rendered when
	// entityFilter is on — feeds the same "debts with unpaid dues" list the
	// legacy QuickPaymentModal used, so no debtService.list() call is needed
	// in this mode.
	const useEntityFilter = entityFilter && !preloadDebtId && !isEditing;
	const [entityAccounts, setEntityAccounts] = useState([]);
	const [entityProperties, setEntityProperties] = useState([]);
	const [searchBy, setSearchBy] = useState('');
	const [selectedEntity, setSelectedEntity] = useState(null);

	const [showReceipt, setShowReceipt] = useState(false);
	const [createdPaymentId, setCreatedPaymentId] = useState(null);

	// Keep the URL mirroring the current selection so a manual pick or clear
	// doesn't leave a stale id from an earlier round trip to be re-injected.
	const syncInjectedParam = (key, id, consumedRef) => {
		const nextValue = id || null;
		if (searchParams.get(key) === nextValue) return;
		if (nextValue) consumedRef.current = nextValue;
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			if (nextValue) next.set(key, nextValue);
			else next.delete(key);
			return next;
		}, { replace: true });
	};

	useEffect(() => {
		let isCurrent = true;

		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				if (isEditing && paymentId && !initialData) {
					const requests = [paymentService.getDetails(paymentId)];
					if (!preloadDebtId) {
						requests.push(debtService.list({ populate: false, paid: false }));
					}
					const results = await Promise.all(requests);
					if (!isCurrent) return;
					const paymentRes = results[0];
					const paymentData = paymentRes.data;
					setForm(paymentToForm(paymentData));
					setDisableDebtDue(false);

					const resolvedDebtId = paymentData.debt?._id || paymentData.debt;
					if (!preloadDebtId) {
						setDebts(results[1]?.data || []);
					}
					if (resolvedDebtId) {
						const duesRes = await debtService.getDuesForDebt(resolvedDebtId);
						if (isCurrent) setGeneratedDues(duesRes.data || []);
					}
					dispatch({ type: 'FETCH_SUCCESS' });
					return;
				}

				if (preloadDebtId) {
					const duesRes = await debtService.getDuesForDebt(preloadDebtId, { paid: false });
					if (!isCurrent) return;
					const dues = duesRes.data || [];
					let debtData = dues[0]?.debt;
					if (!debtData) {
						const debtRes = await debtService.getDetails(preloadDebtId);
						debtData = debtRes.data;
					}
					if (!isCurrent) return;
					setForm((prev) => ({ ...prev, debt: debtData, currency: debtData.currency || '', folder: debtData.owner?.folder || '' }));
					setGeneratedDues(dues);
					setDisableDebtDue(false);
				} else if (useEntityFilter) {
					const { data } = await debtService.getQuickPaymentEntities();
					if (!isCurrent) return;
					setEntityAccounts(data?.accounts || []);
					setEntityProperties(data?.properties || []);
					setDebts(Array.isArray(data?.debts) ? data.debts : []);
				} else {
					const result = await debtService.list({ populate: false, paid: false });
					if (!isCurrent) return;
					setDebts(result.data || []);
					if (initialData) {
						setForm(paymentToForm(initialData));
						if (initialData.debt) {
							setDisableDebtDue(false);
							const debtId = initialData.debt?._id || initialData.debt;
							const duesRes = await debtService.getDuesForDebt(debtId);
							if (isCurrent) setGeneratedDues(duesRes.data || []);
						}
					}
				}
				dispatch({ type: 'FETCH_SUCCESS' });
			} catch (ex) {
				const message = getError(ex);
				if (isCurrent) dispatch({ type: 'FETCH_FAIL', payload: message });
				toast.error(isEditing ? 'No se pudo obtener cobro' : 'No se pudieron obtener las deudas');
			} finally {
				if (isCurrent) dispatch({ type: 'FETCH_DONE' });
			}
		};

		fetchData();
		return () => {
			isCurrent = false;
		};
	}, [isEditing, paymentId, preloadDebtId, useEntityFilter]);

	const updateField = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		setFieldErrors((prev) => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const clearFieldErrors = (...fields) => {
		setFieldErrors((prev) => {
			if (!fields.some((field) => prev[field])) return prev;
			const next = { ...prev };
			fields.forEach((field) => delete next[field]);
			return next;
		});
	};

	// Auto-select a debt created via the "crear nueva deuda" link, which
	// returns here with ?debtId=<id>. debts is already refetched fresh on
	// mount (see the effect above), so find-in-list is enough.
	useEffect(() => {
		if (!injectedDebtId || consumedDebtId.current === injectedDebtId) return;
		const injectedDebt = debts.find((d) => d._id === injectedDebtId);
		if (!injectedDebt) return;
		setForm((prev) => ({ ...prev, debt: injectedDebt, debtDue: null, currency: injectedDebt.currency || '', folder: injectedDebt.owner?.folder || '' }));
		setDisableDebtDue(false);
		setUnpaidAmount(0);
		loadGeneratedDues(injectedDebt);
		consumedDebtId.current = injectedDebtId;
	}, [debts, injectedDebtId]);

	const loadGeneratedDues = async (selectedDebt) => {
		try {
			const { data } = await debtService.getDuesForDebt(selectedDebt._id, { paid: false });
			setGeneratedDues(data || []);
		} catch (ex) {
			console.error(ex);
			toast.error('No se pudo cargar las cuotas de la deuda');
		}
	};

	const handleDebtChange = (selected) => {
		clearFieldErrors('debt', 'debtDue');
		if (!selected || selected.length === 0) {
			setForm((prev) => ({ ...prev, debt: null, debtDue: null, currency: '', folder: '' }));
			setDisableDebtDue(true);
			setUnpaidAmount(0);
			syncInjectedParam('debtId', null, consumedDebtId);
			return;
		}
		const selectedDebt = selected[0];
		setForm((prev) => ({ ...prev, debt: selectedDebt, debtDue: null, currency: selectedDebt.currency || '', folder: selectedDebt.owner?.folder || '' }));
		setDisableDebtDue(false);
		setUnpaidAmount(0);
		syncInjectedParam('debtId', selectedDebt._id, consumedDebtId);
		loadGeneratedDues(selectedDebt);
	};

	const handleDebtDueChange = (selected) => {
		clearFieldErrors('debtDue');
		if (!selected || selected.length === 0) {
			setForm((prev) => ({ ...prev, debtDue: null, amount: 0 }));
			setUnpaidAmount(0);
			return;
		}
		const selectedDebtDue = selected[0];
		setForm((prev) => ({ ...prev, debtDue: selectedDebtDue, amount: selectedDebtDue.unpaidAmount || 0 }));
		setUnpaidAmount(selectedDebtDue.unpaidAmount || 0);
	};

	// Entity-first narrowing (see useEntityFilter above) — lifted from the
	// legacy QuickPaymentModal's handleAccountSelect/handlePropertySelect.
	// Only narrows the Deuda options; never disables the field.
	const handleFilterAccountChange = (selected) => {
		if (!selected || selected.length === 0) {
			setSelectedEntity(null);
			return;
		}
		const account = selected[0];
		setSelectedEntity({ type: 'Cliente', id: account._id, name: account.name || account.customId || '' });
		handleDebtChange(null);
	};

	const handleFilterPropertyChange = (selected) => {
		if (!selected || selected.length === 0) {
			setSelectedEntity(null);
			return;
		}
		const property = selected[0];
		setSelectedEntity({ type: 'Propiedad', id: property._id, name: property.name || property.customId || '' });
		handleDebtChange(null);
	};

	const visibleDebts = !useEntityFilter || !selectedEntity
		? debts
		: debts.filter((debt) => {
			if (selectedEntity.type === 'Cliente') {
				return debt.owner && (debt.owner._id === selectedEntity.id || debt.owner._id?.toString() === selectedEntity.id?.toString());
			}
			if (!debt.contract) return false;
			const propRef = debt.contract.property;
			if (!propRef) return false;
			return (propRef._id?.toString() || propRef?.toString()) === selectedEntity.id?.toString();
		});

	const handleAmountChange = (event) => {
		// Keep the raw string in state so a decimal separator survives while it is being typed.
		const raw = event.target.value;
		const parsed = parseFloat(raw);
		if (raw === '' || !unpaidAmount || (!isNaN(parsed) && parsed <= unpaidAmount)) {
			updateField('amount', raw);
		}
	};

	const uploadComprobante = async () => {
		if (!form.comprobante || (typeof form.comprobante === 'object' && !form.comprobante[0])) {
			toast.warning('Debe seleccionar un archivo');
			return;
		}

		dispatch({ type: 'UPLOAD_REQUEST' });
		const file = form.comprobante[0];
		const formData = new FormData();
		const r = Math.random().toString(36).substring(7);
		formData.append('file', file, (file.name || '').replaceAll(' ', '') + r);
		if (form.comprobante._id) {
			formData.append('documentId', form.comprobante._id);
		}

		try {
			const response = await paymentService.uploadComprobante(formData);
			updateField('comprobante', response.data);
			dispatch({ type: 'UPLOAD_SUCCESS' });
			toast.success('Archivo Subido');
		} catch (ex) {
			dispatch({ type: 'UPLOAD_FAIL' });
			toast.error(getError(ex));
		}
	};

	const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

	const FIELD_LABELS = {
		debt: 'Deuda',
		debtDue: 'Cuota',
		date: 'Fecha',
		amount: 'Monto',
	};

	const getFieldErrors = () => {
		const errors = {};
		if (!form.debt) errors.debt = REQUIRED_FIELD_MESSAGE;
		if (!form.debtDue) errors.debtDue = REQUIRED_FIELD_MESSAGE;
		if (!form.date) errors.date = REQUIRED_FIELD_MESSAGE;
		if (!form.amount || form.amount <= 0) errors.amount = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const handleSave = async (event) => {
		event.preventDefault();
		if (loading || saving) return;

		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			const validationError = `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`;
			dispatch({ type: 'SAVE_FAIL', payload: validationError });
			toast.error(validationError);
			return;
		}
		setFieldErrors({});

		const payload = {
			amount: Number(form.amount),
			date: moment.utc(form.date).format(),
			debt: form.debt?._id || form.debt,
			folder: form.folder,
			observations: form.observations,
			comprobante: form.comprobante?._id || form.comprobante,
			debtDue: form.debtDue?._id || form.debtDue,
			currency: form.currency,
			paidBy: form.paidBy,
			paymentMethod: form.paymentMethod,
		};

		dispatch({ type: 'SAVE_REQUEST' });
		try {
			if (isEditing) {
				await paymentService.edit(paymentId || initialData?._id, payload);
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success('Cobro actualizado correctamente');
				if (onSaved) onSaved();
			} else {
				const { data } = await paymentService.create(payload);
				dispatch({ type: 'SAVE_SUCCESS' });
				toast.success('Cobro creado');
				setCreatedPaymentId(data.payment._id);
				setShowReceipt(true);
			}
		} catch (ex) {
			dispatch({ type: 'SAVE_FAIL', payload: getError(ex) });
			console.error(getError(ex));
			toast.error(isEditing ? 'Error al actualizar cobro' : 'Error al crear cobro');
		}
	};

	if (showReceipt) {
		return <PaymentReceiptStep paymentId={createdPaymentId} onDone={onSaved || onCancel} />;
	}

	if (loading) {
		return <DetailPageLoader entityLabel='editor' />;
	}

	const resolvedSaveLabel = saveLabel || (isEditing ? 'Guardar' : 'Agregar');
	const availableDues = (generatedDues || []).filter((due) => !due.paid && !isCancelledByClose(due));

	return (
		<Form id='payment-editor-form' onSubmit={handleSave} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEditing ? 'Actualizá los datos del cobro.' : 'Completá los datos para registrar el cobro.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving}>
						{cancelLabel}
					</Button>
					<Button variant='primary' icon={isEditing ? <FaRegSave /> : <MdAdd />} type='submit' disabled={saving || loading}>
						{saving ? 'Guardando...' : resolvedSaveLabel}
					</Button>
				</div>
			</div>

			{error && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{error}
				</div>
			)}

			{useEntityFilter && (
				<Section title='Buscar deuda por' description='Opcional: filtrá las deudas por cliente o propiedad.'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
						<FormField id='payment-search-by' label='Buscar por'>
							<Select
								id='payment-search-by'
								options={[{ value: 'account', label: 'Cliente' }, { value: 'property', label: 'Propiedad' }]}
								value={searchBy}
								onChange={(event) => {
									setSearchBy(event.target.value);
									setSelectedEntity(null);
								}}
								placeholder='Elegí una opción'
								disabled={saving}
							/>
						</FormField>
						{searchBy === 'account' && (
							<FormField id='payment-filter-account' label='Cliente'>
								<CustomTypeahead
									id='payment-filter-account'
									labelKey={(option) => option?.name || ''}
									onChange={handleFilterAccountChange}
									options={entityAccounts}
									placeholder='Buscar cliente...'
									emptyLabel='Sin clientes con cuotas pendientes'
									linkToCreate=''
									newOption={null}
									disabled={saving}
									variant='entity'
								/>
							</FormField>
						)}
						{searchBy === 'property' && (
							<FormField id='payment-filter-property' label='Propiedad'>
								<CustomTypeahead
									id='payment-filter-property'
									labelKey={(option) => option?.name || option?.customId || ''}
									onChange={handleFilterPropertyChange}
									options={entityProperties}
									placeholder='Buscar propiedad...'
									emptyLabel='Sin propiedades con cuotas pendientes'
									linkToCreate=''
									newOption={null}
									disabled={saving}
									variant='entity'
								/>
							</FormField>
						)}
					</div>
				</Section>
			)}

			<Section title='Deuda y cuota'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='payment-debt' label='Deuda' required>
						<CustomTypeahead
							id='payment-debt'
							labelKey={(option) => option?.customId || ''}
							onChange={handleDebtChange}
							options={visibleDebts}
							selected={form.debt ? [form.debt] : []}
							placeholder='Buscar deuda...'
							linkToCreate={`/AdminScreen/newDebt?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
							newOption='nueva deuda'
							disabled={saving || !!preloadDebtId}
							variant='entity'
						/>
						<FieldError message={fieldErrors.debt} />
					</FormField>
					<FormField id='payment-debt-due' label='Cuota Deuda' required>
						<CustomTypeahead
							id='payment-debt-due'
							labelKey={(option) => option?.customId || ''}
							onChange={handleDebtDueChange}
							options={availableDues}
							selected={form.debtDue ? [form.debtDue] : []}
							placeholder='Buscar cuota...'
							disabled={saving || disableDebtDue}
							variant='entity'
						/>
						<FieldError message={fieldErrors.debtDue} />
					</FormField>
				</div>
			</Section>

			<Section title='Datos del cobro'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='payment-amount' label='Monto' required>
						<Form.Control
							id='payment-amount'
							type='number'
							step='any'
							min={0}
							max={unpaidAmount || undefined}
							className={controlClass}
							value={form.amount}
							onChange={handleAmountChange}
						/>
						{unpaidAmount != null && unpaidAmount > 0 && (
							<Form.Text className='tw:text-ink-3'>Máximo: {unpaidAmount}</Form.Text>
						)}
						<FieldError message={fieldErrors.amount} />
					</FormField>
					<FormField id='payment-date' label='Fecha' required>
						<Form.Control
							id='payment-date'
							type='date'
							className={controlClass}
							value={toDateInputValue(form.date)}
							onChange={(event) => updateField('date', event.target.value ? new Date(event.target.value) : null)}
						/>
						<FieldError message={fieldErrors.date} />
					</FormField>
					<FormField id='payment-currency' label='Moneda'>
						<Select
							id='payment-currency'
							options={currencies}
							value={form.currency}
							onChange={(event) => updateField('currency', event.target.value)}
							placeholder='Elegí Moneda'
							disabled={saving}
						/>
					</FormField>
					<FormField id='payment-folder' label='Carpeta'>
						<Form.Control
							id='payment-folder'
							className={controlClass}
							value={form.folder}
							onChange={(event) => updateField('folder', event.target.value)}
						/>
					</FormField>
					<FormField id='payment-paid-by' label='Quien realiza el pago'>
						<Form.Control
							id='payment-paid-by'
							className={controlClass}
							value={form.paidBy}
							onChange={(event) => updateField('paidBy', event.target.value)}
							placeholder='Nombre del pagador'
						/>
					</FormField>
					<FormField id='payment-method' label='Medio de pago'>
						<Form.Control
							id='payment-method'
							className={controlClass}
							value={form.paymentMethod}
							onChange={(event) => updateField('paymentMethod', event.target.value)}
							placeholder='Ej: Efectivo, Transferencia'
						/>
					</FormField>
				</div>
			</Section>

			<Section title='Comprobante'>
				<div className='tw:flex tw:items-end tw:gap-3'>
					<FormField id='payment-comprobante' label='Comprobante' className='tw:flex-1'>
						<Form.Control
							id='payment-comprobante'
							type='file'
							className={controlClass}
							onChange={(event) => updateField('comprobante', event.target.files)}
						/>
					</FormField>
					<Button onClick={uploadComprobante} disabled={uploading || !form.comprobante}>
						{uploading ? 'Subiendo...' : 'Subir'}
					</Button>
				</div>
			</Section>

			<Section title='Observaciones' description='Notas internas o información adicional del cobro.'>
				<FormField id='payment-observations' label='Observaciones'>
					<Form.Control
						id='payment-observations'
						as='textarea'
						rows={8}
						className={textareaClass}
						value={form.observations}
						onChange={(event) => updateField('observations', event.target.value)}
					/>
				</FormField>
			</Section>
		</Form>
	);
};

PaymentEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']),
	paymentId: PropTypes.string,
	initialData: PropTypes.object,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	cancelLabel: PropTypes.string,
	saveLabel: PropTypes.string,
	entityFilter: PropTypes.bool,
	preloadDebtId: PropTypes.string,
};

export default PaymentEditor;
